import type { FilterGroupData } from "./advanced-filter-builder/constants";
import {
  hasMiqaatRsvpAudienceIncluded,
  isMiqaatRsvpFilterEnvelope,
  type MiqaatRsvpFilterEnvelope,
} from "./miqaat-rsvp-filter-criteria";
import type { BroadcastParameter } from "./utils";

export function filterGroupLabel(
  filter: FilterGroupData | null | undefined,
  title: string
): string {
  const rules = filter?.rules?.length ?? 0;
  const groups = filter?.groups?.length ?? 0;
  if (rules === 0 && groups === 0) return `${title}: all`;
  return `${title}: ${rules} rule(s), ${groups} group(s)`;
}

export type FilterStatsSummary = { rulesCount: number; groupsCount: number; label?: string } | null;

export type RecipientSourceSummary = {
  hasFilterCriteria: boolean;
  recipientSource: string;
  filterStats: FilterStatsSummary;
};

/**
 * Plain-English summary of "who does this broadcast target" from its kind + filterCriteria.
 * Shared by the create flow's Summary step and the Show page's review view, so both describe the
 * same broadcast the same way instead of one showing prose and the other raw JSON.
 */
export function describeBroadcastRecipientSource(params: {
  broadcastKind?: string;
  filterCriteria?: FilterGroupData | MiqaatRsvpFilterEnvelope | null | undefined;
  recipientItsIds?: string[] | null | undefined;
}): RecipientSourceSummary {
  const { broadcastKind, filterCriteria, recipientItsIds } = params;
  const isMiqaatRsvp = broadcastKind === "MIQAAT_RSVP";
  const isEventRsvp = broadcastKind === "EVENT_RSVP";
  const isFmbOutstanding = broadcastKind === "FMB_OUTSTANDING";
  const isSabilOutstanding = broadcastKind === "SABIL_OUTSTANDING";
  const isMuttavatteenOutstanding = broadcastKind === "MUTTAVATTEEN_OUTSTANDING";

  if (isFmbOutstanding) {
    return {
      hasFilterCriteria: true,
      recipientSource: "FMB outstanding filters",
      filterStats: null,
    };
  }
  if (isSabilOutstanding) {
    return {
      hasFilterCriteria: true,
      recipientSource: "Sabil outstanding filters",
      filterStats: null,
    };
  }
  if (isMuttavatteenOutstanding) {
    return {
      hasFilterCriteria: true,
      recipientSource: "Mutawatteen outstanding filters",
      filterStats: null,
    };
  }

  const csvItsCount = Array.isArray(recipientItsIds) ? recipientItsIds.length : 0;
  if (isMiqaatRsvp && csvItsCount > 0) {
    return {
      hasFilterCriteria: true,
      recipientSource: "CSV ITS list",
      filterStats: {
        rulesCount: 0,
        groupsCount: 0,
        label: `${csvItsCount} ITS number${csvItsCount === 1 ? "" : "s"} matched against ITS directory + Mutawatteen`,
      },
    };
  }

  if (isMiqaatRsvpFilterEnvelope(filterCriteria)) {
    const included = hasMiqaatRsvpAudienceIncluded(filterCriteria);
    const parts: string[] = [];
    if (filterCriteria.itsDirectory.include) {
      parts.push(filterGroupLabel(filterCriteria.itsDirectory.filter, "ITS directory"));
    }
    if (filterCriteria.muttavatteen.include) {
      parts.push(filterGroupLabel(filterCriteria.muttavatteen.filter, "Mutawatteen"));
    }
    return {
      hasFilterCriteria: included,
      recipientSource: included ? "Miqaat RSVP audiences" : "No audience selected",
      filterStats: included ? { rulesCount: 0, groupsCount: 0, label: parts.join(" · ") } : null,
    };
  }

  const group = filterCriteria as FilterGroupData | undefined;
  const hasRules = !!group?.rules && group.rules.length > 0;
  const hasGroups = !!group?.groups && group.groups.length > 0;
  const hasFilter = hasRules || hasGroups;

  let source = "None";
  if (hasFilter) {
    source = "Filter";
  } else if (isEventRsvp) {
    source = "All registrations";
  }

  return {
    hasFilterCriteria: hasFilter,
    recipientSource: source,
    filterStats: hasFilter
      ? { rulesCount: group?.rules?.length || 0, groupsCount: group?.groups?.length || 0 }
      : null,
  };
}

/**
 * The persisted `recipientIds` column (API's `buildRecipientIdsPayload`) is either a plain ITS
 * array or, when CSV row data was attached, `{ itsIds, csvDataByItsId }`. Normalize either shape
 * to the plain array `describeBroadcastRecipientSource` expects for its "CSV ITS list" case.
 */
export function normalizeRecipientIdsToItsIds(recipientIds: unknown): string[] | undefined {
  if (Array.isArray(recipientIds)) {
    return recipientIds.filter((id): id is string => typeof id === "string");
  }
  if (recipientIds && typeof recipientIds === "object" && "itsIds" in recipientIds) {
    const itsIds = (recipientIds as { itsIds?: unknown }).itsIds;
    return Array.isArray(itsIds)
      ? itsIds.filter((id): id is string => typeof id === "string")
      : undefined;
  }
  return undefined;
}

export type ParameterSummaryRow = { key: string; mode: "text" | "column"; value: string };

/** Plain-English rows for the stored `parameters` map — used to replace a raw JSON dump. */
export function describeBroadcastParameters(
  parameters: Record<string, BroadcastParameter> | null | undefined
): ParameterSummaryRow[] {
  if (!parameters || typeof parameters !== "object") return [];

  return Object.keys(parameters)
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
    .map((key): ParameterSummaryRow | null => {
      const param = parameters[key];
      if (param && typeof param === "object") {
        if (param.type === "text") {
          const value = String(param.value ?? "").trim();
          return value ? { key, mode: "text", value } : null;
        }
        if (param.type === "column") {
          const column = String(param.column ?? "").trim();
          if (!column) return null;
          const label =
            param.columnSource === "csv" ? `CSV column: ${column}` : `ITS column: ${column}`;
          return { key, mode: "column", value: label };
        }
        return null;
      }
      const value = String(param ?? "").trim();
      return value ? { key, mode: "text", value } : null;
    })
    .filter((row): row is ParameterSummaryRow => row !== null);
}
